import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import {
  Center,
  Flex,
  Icon,
  Button,
  useDisclosure,
  Box,
} from '@chakra-ui/react';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { KText } from '@/ui-library/components';
import { HtmlRenderer } from '@/components/ats/HtmlRenderer/HtmlRenderer';

const OfferRequestNotificationWrapper = ({
  notification = {},
  onClose = () => {},
  components = {},
}) => {
  const { custom_attributes } = notification || {};
  let data = {};

  try {
    data = JSON.parse(decode(custom_attributes || ''));
  } catch (error) {
    console.error(
      'Error decoding offer request notification attributes:',
      error
    );
  }

  const {
    candidate = {},
    offer = {},
    job = {},
    requisition = {},
    custom_fields = {},
    additional_compensation = {},
    candidate_name,
    candidate_email,
    candidate_phone,
    offer_id: offerId,
    offer_job_title,
    requester_name,
    job_name,
  } = data || {};

  const finalOfferId = offerId || offer?.id;
  const { OfferApprovalView } = components || {};
  const offerApprovalActions = useDisclosure();

  // Candidate details
  const candidateDetails = [
    { label: 'Name', value: candidate_name || candidate?.name },
    { label: 'Email', value: candidate_email || candidate?.email },
    { label: 'Phone', value: candidate_phone || candidate?.phone },
  ].filter((item) => item.value);

  const offerDetails = [
    { label: 'Offer date', value: offer?.date },
    { label: 'Salary', value: offer?.salary },
    { label: 'Start date', value: offer?.start_date },
    { label: 'Expiration date', value: offer?.expiration_date },
    { label: 'Additional bonus', value: offer?.additional_bonus },
  ].filter((item) => item.value);

  // Additional compensation (equity)
  const equityDetails = [];

  // Additional compensation from additional_compensation object
  if (additional_compensation) {
    if (additional_compensation?.equity) {
      equityDetails.push({
        label: 'Equity',
        value: additional_compensation?.equity,
      });
    }
    if (additional_compensation?.equity_cliff) {
      equityDetails.push({
        label: 'Equity cliff',
        value: additional_compensation.equity_cliff,
      });
    }
    if (additional_compensation?.vesting_period) {
      equityDetails.push({
        label: 'Vesting period',
        value: additional_compensation.vesting_period,
      });
    }
    if (additional_compensation?.equity_strike_price) {
      equityDetails.push({
        label: 'Equity strike price',
        value: additional_compensation.equity_strike_price,
      });
    }
    if (additional_compensation?.post_cliff_vesting_cycle) {
      equityDetails.push({
        label: 'Post cliff vesting cycle',
        value: additional_compensation.post_cliff_vesting_cycle,
      });
    }
  }

  // Job details
  const jobDetails = [
    {
      label: 'Title',
      value: offer_job_title || offer?.job_title || job?.name || job_name,
    },
    { label: 'Reporting manager', value: job?.reporting_manager },
    { label: 'Department', value: job?.department },
    { label: 'Office', value: job?.office },
    { label: 'Req ID', value: job?.req_id },
  ].filter((item) => item.value);

  // Additional details (custom fields)
  // Supports new array format [{label, value, position}] and old map format {label: value}
  const additionalDetails = Array.isArray(custom_fields)
    ? custom_fields
        .filter((cf) => cf.value)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(({ label, value }) => ({ label, value: String(value) }))
    : Object.entries(custom_fields || {}).map(([key, value]) => ({
        label: key,
        value: String(value),
      }));

  const handleApproveReject = () => {
    offerApprovalActions?.onOpen();
  };

  return (
    <>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
          <Icon as={LayersThree02} color="primary-500" />
        </Center>
        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
          <KText>
            <KText as="span" fontWeight="semiBold">
              {requester_name}
            </KText>{' '}
            requests offer approval for{' '}
            <KText as="span" fontWeight="semiBold">
              {candidate_name || candidate?.name}
            </KText>{' '}
            in the{' '}
            <KText as="span" fontWeight="semiBold">
              {offer_job_title || offer?.job_title || job?.name || job_name}
            </KText>{' '}
            job
          </KText>

          <RequisitionDetailsSection title="Details">
            <Flex flexDir="column" gap="24px">
              <Flex
                flexDir="column"
                gap="16px"
                p="16px"
                bg="white"
                width="100%"
              >
                {candidateDetails.length > 0 && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Candidate details
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />
                    <RequisitionDetailsList data={candidateDetails} p="0" />
                  </Flex>
                )}

                {offerDetails.length > 0 && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Offer details
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />

                    <RequisitionDetailsList data={offerDetails} p="0" />
                    {requisition?.salary_exceeds_max && (
                      <KText size="sm-medium" color="secondary-700">
                        The salary in this offer is higher than the salary range
                        in the requisition
                      </KText>
                    )}
                    {requisition?.salary_range && (
                      <Flex flexDir="row" gap="8px" minW="0" overflow="hidden">
                        <KText
                          size="sm-semi-bold"
                          minW="fit-content"
                          flexShrink={0}
                        >
                          Salary range:
                        </KText>
                        <KText
                          size="sm-medium"
                          flex="1"
                          minW="0"
                          isTruncated
                          enableTooltip
                          tooltipText={requisition.salary_range}
                        >
                          {requisition.salary_range}
                          {requisition.salary_range &&
                            !requisition.salary_range.includes('USD') &&
                            ' (USD)'}
                        </KText>
                      </Flex>
                    )}
                  </Flex>
                )}

                {equityDetails.length > 0 && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Additional compensation
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />

                    <RequisitionDetailsList data={equityDetails} p="0" />
                  </Flex>
                )}

                {jobDetails.length > 0 && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Job details
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />

                    <RequisitionDetailsList data={jobDetails} p="0" />
                  </Flex>
                )}

                {additionalDetails.length > 0 && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Additional details
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />

                    <RequisitionDetailsList data={additionalDetails} p="0" />
                  </Flex>
                )}

                {offer?.approval_request_note && (
                  <Flex flexDir="column" gap="8px">
                    <KText
                      variant="heading_4"
                      color="secondary-900"
                      fontWeight="semiBold"
                    >
                      Approval note
                    </KText>
                    <Box borderBottom="1px solid" borderColor="border-color" />

                    <HtmlRenderer htmlString={offer.approval_request_note} />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </RequisitionDetailsSection>

          <NotificationTime notification={notification} />

          <Flex gap="8px">
            <Button
              variant="primary"
              onClick={handleApproveReject}
              size="sm"
              data-test-id="approve-offer-button"
            >
              Approve
            </Button>
            <Button
              variant="danger"
              onClick={handleApproveReject}
              size="sm"
              data-test-id="reject-offer-button"
            >
              Reject
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {offerApprovalActions?.isOpen && OfferApprovalView && (
        <OfferApprovalView
          modalActions={offerApprovalActions}
          offerId={finalOfferId}
          onSuccess={() => {
            offerApprovalActions?.onClose();
            onClose();
          }}
        />
      )}
    </>
  );
};

export const OfferRequestNotification = ({
  notification = {},
  onClose = () => {},
  components,
}) => {
  return (
    <NotificationCard notification={notification}>
      <OfferRequestNotificationWrapper
        notification={notification}
        onClose={onClose}
        components={components}
      />
    </NotificationCard>
  );
};
